import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CategoryPicker, categoryLabel } from '../components/CategoryPicker'
import { Screen } from '../components/Layout'
import { Button, Card, ErrorBanner, Field, Spinner, inputClass } from '../components/ui'
import { uploadStatement } from '../lib/api'
import { money, parseAmount, shortDate } from '../lib/format'
import {
  useCategories,
  useCommitImport,
  useCreateImport,
  useImportJob,
  useMe,
  usePatchImportRows,
} from '../lib/queries'
import type { CommitResponse, ImportRow, PossibleDuplicate } from '../lib/types'

/**
 * CSV upload and review (requirement 4).
 *
 * Nothing is written to the ledger until 匯入 is pressed. The parse produces a
 * job the user edits in place -- category, include, split -- and the commit is the
 * only step that creates expenses. That ordering is what makes an LLM guess
 * acceptable: a wrong category costs a tap, not a corrupted month.
 *
 * Rows the parser could not use are listed rather than hidden, with the statement
 * total next to the imported total, because a silently dropped row is a number
 * that never reconciles.
 */
export function ImportPage() {
  const [jobId, setJobId] = useState<string>()
  const [uploadError, setUploadError] = useState<unknown>()
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<CommitResponse>()
  const fileInput = useRef<HTMLInputElement>(null)

  const create = useCreateImport()
  const job = useImportJob(jobId, { poll: true })
  const commit = useCommitImport(jobId ?? '')

  async function start(file: File): Promise<void> {
    setUploadError(undefined)
    setResult(undefined)
    setUploading(true)
    try {
      const created = await create.mutateAsync()
      if (!created.upload) {
        throw new Error(
          '這個環境沒有設定上傳位置（本機開發請啟動 dev API），無法上傳帳單。',
        )
      }
      await uploadStatement(created.upload, file)
      // Only now is the job worth polling: the parse Lambda is triggered by the
      // upload, so a jobId set before the PUT lands would poll an empty job.
      setJobId(created.jobId)
    } catch (err) {
      setUploadError(err)
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const status = job.data?.job.status

  return (
    <Screen title="匯入帳單">
      <div className="space-y-4">
        <ErrorBanner error={uploadError ?? create.error ?? commit.error} />

        {result ? (
          <Card className="space-y-2 p-4">
            <p className="text-lg">匯入完成</p>
            <p className="text-sm text-muted">
              新增 {result.committed} 筆，共 {money(result.importedMinor)}。
            </p>
            {result.duplicatesSkipped > 0 ? (
              <p className="text-sm text-muted">
                {result.duplicatesSkipped} 筆已經匯入過，這次跳過。
              </p>
            ) : null}
            {result.excluded > 0 ? (
              <p className="text-sm text-muted">{result.excluded} 筆你選擇不匯入。</p>
            ) : null}
            <Link to="/" className="inline-block pt-2 text-sm text-accent">
              查看帳本
            </Link>
          </Card>
        ) : null}

        {!jobId || status === 'FAILED' ? (
          <Card className="space-y-3 p-4">
            <p className="text-sm">
              上傳信用卡帳單 CSV。分期付款與外幣未出帳明細會自動略過，其他每一筆都會先讓你確認分類。
            </p>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              className="block w-full text-sm text-muted file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-4 file:py-2 file:text-ink"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void start(file)
              }}
            />
            {uploading ? <Spinner label="上傳中" /> : null}
            <p className="text-xs text-muted">檔案上限 2 MB，只會用來讀取這份帳單。</p>
          </Card>
        ) : null}

        {status === 'PENDING' || status === 'PARSING' ? (
          <Card className="p-4">
            <Spinner label="正在讀取帳單並判斷分類" />
            <p className="text-xs text-muted">通常幾秒鐘就好，可以留在這個畫面等。</p>
          </Card>
        ) : null}

        {status === 'FAILED' ? (
          <ErrorBanner
            error={new Error(job.data?.job.error ?? '帳單解析失敗，請確認這是信用卡帳單 CSV。')}
          />
        ) : null}

        {job.data && (status === 'READY' || status === 'COMMITTED') ? (
          <ReviewTable
            jobId={job.data.job.jobId}
            rows={job.data.rows}
            summary={job.data.job}
            committed={status === 'COMMITTED'}
            onCommit={() =>
              commit.mutate(undefined, {
                onSuccess: (r) => {
                  setResult(r)
                  setJobId(undefined)
                },
              })
            }
            committing={commit.isPending}
          />
        ) : null}
      </div>
    </Screen>
  )
}

function ReviewTable({
  jobId,
  rows,
  summary,
  committed,
  onCommit,
  committing,
}: {
  jobId: string
  rows: ImportRow[]
  summary: {
    rowCount?: number
    totalMinor?: number
    statementTotalMinor?: number
    skippedInstallmentCount?: number
    skippedInstallmentMinor?: number
    skipped: { reason: string; rawMerchant: string; date?: string; amountMinor?: number }[]
  }
  committed: boolean
  onCommit: () => void
  committing: boolean
}) {
  const taxonomy = useCategories()
  const me = useMe()
  const patch = usePatchImportRows(jobId)
  const [editing, setEditing] = useState<number>()
  // A half-typed amount is not a number, so the share lives as a string until it
  // is committed on blur. Only the open row has a draft: one row is editable at a
  // time, which is also what stops a stale share leaking onto the next row. The
  // note is held the same way, for the same reason -- a patch per keystroke.
  const [shareDraft, setShareDraft] = useState('')
  const [noteDraft, setNoteDraft] = useState('')

  const included = rows.filter((r) => r.include)
  const includedMinor = included.reduce((sum, r) => sum + r.amountMinor, 0)
  const members = me.data?.members ?? []

  // Counted at the top as well as flagged per row: on a 28-row statement a badge
  // on row 19 is a badge nobody sees. Split by kind, because one needs a decision
  // and the other is already handled.
  const alreadyImported = included.filter((r) => r.possibleDuplicate?.reason === 'imported').length
  const maybeDuplicate = included.filter(
    (r) => r.possibleDuplicate && r.possibleDuplicate.reason !== 'imported',
  ).length

  function openRow(row: ImportRow): void {
    if (editing === row.rowIndex) {
      setEditing(undefined)
      return
    }
    setEditing(row.rowIndex)
    // `money` puts thousand separators in and `parseAmount` takes them out, so an
    // existing share round-trips through the input unchanged.
    setShareDraft(
      row.splitPlan?.shareMinor === undefined
        ? ''
        : money(row.splitPlan.shareMinor, row.currency),
    )
    setNoteDraft(row.note ?? '')
  }

  function commitNote(row: ImportRow): void {
    const next = noteDraft.trim()
    if (next === (row.note ?? '')) return
    // `null` clears it: an empty string stored would win the `note ?? merchant`
    // fallback in the split list and label the row with nothing.
    patch.mutate([{ rowIndex: row.rowIndex, note: next === '' ? null : next }])
  }

  /** The typed share in minor units, read in the row's own currency. */
  function draftShare(row: ImportRow): number | undefined {
    return shareDraft.trim() === '' ? undefined : parseAmount(shareDraft, row.currency)
  }

  /** Committed on blur rather than per keystroke: each patch is a network write. */
  function commitShare(row: ImportRow): void {
    const pairId = row.splitPlan?.pairId
    if (!pairId) return
    const shareMinor = draftShare(row)
    // A half-typed or impossible value is left in the box with the error beside
    // it, so the row keeps the share it had until the user makes it valid.
    if (shareDraft.trim() !== '' && (shareMinor === undefined || shareMinor > row.amountMinor)) {
      return
    }
    if (shareMinor === row.splitPlan?.shareMinor) return
    patch.mutate([
      {
        rowIndex: row.rowIndex,
        // Omitting `shareMinor` is what asks for an equal split: the server
        // recomputes it from the row's own amount rather than trusting a number
        // the client worked out.
        splitPlan: shareMinor === undefined ? { pairId } : { pairId, shareMinor },
      },
    ])
  }

  /** What the row is called on screen, and so also what it is called to a reader. */
  const labelOf = (row: ImportRow): string => row.note ?? row.merchant

  return (
    <>
      <Card className="space-y-1 p-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-muted">將匯入</span>
          <span className="tnum text-lg">
            {included.length} 筆 · {money(includedMinor)}
          </span>
        </div>
        {summary.statementTotalMinor !== undefined ? (
          <div className="flex items-baseline justify-between text-xs text-muted">
            <span>帳單本期總額</span>
            <span className="tnum">{money(summary.statementTotalMinor)}</span>
          </div>
        ) : null}
        {summary.skippedInstallmentCount ? (
          <div className="flex items-baseline justify-between text-xs text-muted">
            <span>分期付款（略過）</span>
            <span className="tnum">
              {summary.skippedInstallmentCount} 筆 · {money(summary.skippedInstallmentMinor ?? 0)}
            </span>
          </div>
        ) : null}
        {maybeDuplicate > 0 ? (
          <p className="pt-1 text-xs text-owing">
            {maybeDuplicate} 筆可能和帳本裡已有的紀錄重複，請確認後再匯入。
          </p>
        ) : null}
        {alreadyImported > 0 ? (
          <p className="pt-1 text-xs text-muted">
            {alreadyImported} 筆之前已經匯入過，匯入時會自動跳過。
          </p>
        ) : null}
      </Card>

      <ErrorBanner error={patch.error} />

      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.rowIndex}
            className={`rounded-2xl bg-ink-soft/70 p-3 ring-1 ring-ink-line/60 ${
              row.include ? '' : 'opacity-50'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 size-5 accent-amber-500"
                checked={row.include}
                disabled={committed || patch.isPending}
                aria-label={`匯入 ${labelOf(row)}`}
                onChange={(e) =>
                  patch.mutate([{ rowIndex: row.rowIndex, include: e.target.checked }])
                }
              />
              {/*
                The whole row opens the editor, not just the category label.
                Two reasons it had to stop being a link on the category: on a
                phone that word is a ~40px target, and it named the wrong thing --
                the panel behind it also holds the split, which nobody would think
                to look for under 「餐飲」. The checkbox stays outside, since one
                tap must not mean both "edit this" and "drop this".
              */}
              <button
                type="button"
                className="flex min-w-0 flex-1 items-start gap-3 text-left"
                aria-label={`編輯 ${labelOf(row)}`}
                aria-expanded={editing === row.rowIndex}
                disabled={committed}
                onClick={() => openRow(row)}
              >
                <span className="min-w-0 flex-1">
                  {/*
                    Note first, merchant demoted to the meta line -- the same order
                    the split list uses. The merchant is never dropped, though: it
                    is what the row is reconciled against on the statement.
                  */}
                  <span className="block truncate text-sm">{labelOf(row)}</span>
                  <span className="block truncate text-xs text-muted">
                    {shortDate(row.date)} · {row.note ? `${row.merchant} · ` : ''}
                    <span className="text-accent">
                      {categoryLabel(taxonomy.data, row.categoryId, row.subCategoryId)}
                    </span>
                    {row.categorySource === 'llm' && row.confidence < 0.7 ? (
                      <span className="ml-1 text-owing">請確認</span>
                    ) : null}
                  </span>
                  {row.originalCurrency ? (
                    <span className="block truncate text-xs text-muted">
                      原幣 {row.originalAmount} {row.originalCurrency}
                      {row.feeMinor ? ` · 含手續費 ${money(row.feeMinor)}` : ''}
                    </span>
                  ) : null}
                  {row.splitPlan ? (
                    <span className="block text-xs text-owed">
                      與{' '}
                      {members.find((m) => m.pairId === row.splitPlan?.pairId)?.displayName ??
                        '成員'}{' '}
                      分帳
                      {row.splitPlan.shareMinor !== undefined
                        ? `：對方 ${money(row.splitPlan.shareMinor, row.currency)}`
                        : '（平分）'}
                    </span>
                  ) : null}
                  {row.possibleDuplicate ? (
                    <DuplicateHint duplicate={row.possibleDuplicate} />
                  ) : null}
                </span>
                <span className="tnum text-sm">{money(row.amountMinor, row.currency)}</span>
                <span aria-hidden className="text-xs text-muted">
                  {editing === row.rowIndex ? '▴' : '▾'}
                </span>
              </button>
            </div>

            {editing === row.rowIndex ? (
              <div className="mt-3 space-y-3">
                {taxonomy.data ? (
                  <CategoryPicker
                    taxonomy={taxonomy.data}
                    categoryId={row.categoryId}
                    {...(row.subCategoryId ? { subCategoryId: row.subCategoryId } : {})}
                    onChange={(next) =>
                      patch.mutate([
                        {
                          rowIndex: row.rowIndex,
                          categoryId: next.categoryId,
                          // Explicit `null` clears the old sub, which would
                          // otherwise stay attached to a different main.
                          subCategoryId: next.subCategoryId ?? null,
                        },
                      ])
                    }
                  />
                ) : null}

                {/*
                  Above the split, because it is usually why it is being typed:
                  the statement names the acquirer, not the shop, and the other
                  member cannot open the payer's expense to work out what
                  「ACQ*PXMART」 was.
                */}
                <Field
                  label="備註（選填）"
                  hint={
                    row.splitPlan
                      ? '分帳對象會看到這段說明。'
                      : '帳單上的商店名稱常常看不出是什麼，可以在這裡寫清楚。'
                  }
                >
                  <input
                    className={inputClass}
                    value={noteDraft}
                    placeholder={row.merchant}
                    maxLength={1000}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onBlur={() => commitNote(row)}
                  />
                </Field>

                {members.length > 0 ? (
                  <select
                    className="w-full rounded-xl bg-ink px-3 py-2 text-sm text-paper ring-1 ring-ink-line"
                    aria-label="分帳對象"
                    value={row.splitPlan?.pairId ?? ''}
                    onChange={(e) => {
                      setShareDraft('')
                      patch.mutate([
                        {
                          rowIndex: row.rowIndex,
                          // A newly chosen member starts on an equal split; the
                          // share below is the way to change that.
                          splitPlan: e.target.value ? { pairId: e.target.value } : null,
                        },
                      ])
                    }}
                  >
                    <option value="">不分帳</option>
                    {members.map((member) => (
                      <option key={member.pairId} value={member.pairId}>
                        與 {member.displayName} 分帳
                      </option>
                    ))}
                  </select>
                ) : null}

                {row.splitPlan ? (
                  <>
                    <Field
                      label="對方負擔金額（選填）"
                      hint={`留空則各付一半：對方 ${money(
                        Math.floor(row.amountMinor / 2),
                        row.currency,
                      )}。`}
                    >
                      <input
                        className={`${inputClass} tnum`}
                        inputMode="decimal"
                        value={shareDraft}
                        placeholder="平分"
                        onChange={(e) => setShareDraft(e.target.value)}
                        onBlur={() => commitShare(row)}
                      />
                    </Field>
                    {(draftShare(row) ?? 0) > row.amountMinor ? (
                      <p className="text-xs text-owing">對方負擔金額不能超過這筆的金額。</p>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {summary.skipped.length > 0 ? (
        <Card className="p-4">
          <p className="text-sm text-muted">略過 {summary.skipped.length} 筆</p>
          <ul className="mt-2 space-y-1 text-xs text-muted">
            {summary.skipped.map((skip, i) => (
              <li key={`${skip.rawMerchant}-${i}`} className="truncate">
                {skip.date ? `${shortDate(skip.date)} · ` : ''}
                {skip.rawMerchant} · {SKIP_REASON[skip.reason] ?? skip.reason}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {!committed ? (
        <Button
          className="w-full"
          disabled={included.length === 0 || committing || patch.isPending}
          onClick={onCommit}
        >
          {committing ? '匯入中…' : `匯入 ${included.length} 筆`}
        </Button>
      ) : (
        <p className="text-center text-sm text-muted">這份帳單已經匯入過了。</p>
      )}
    </>
  )
}

/**
 * Names what the row might already be, and then gets out of the way.
 *
 * Deliberately not an automatic untick. The match is a guess -- near the same
 * day, near the same amount -- and a wrong guess that dropped a real expense
 * would be invisible. The checkbox beside the row is already the control; this
 * only gives the user a reason to reach for it.
 */
function DuplicateHint({ duplicate }: { duplicate: PossibleDuplicate }) {
  const existing = [shortDate(duplicate.date), duplicate.label, money(duplicate.amountMinor)]
    .filter(Boolean)
    .join(' ')
  return duplicate.reason === 'imported' ? (
    <p className="text-xs text-muted">已匯入過（{existing}），匯入時會跳過</p>
  ) : (
    <p className="text-xs text-owing">可能與「{existing}」重複</p>
  )
}

const SKIP_REASON: Record<string, string> = {
  installment: '分期付款',
  'no-amount': '沒有金額',
  unparseable: '無法解析',
}
